import {createApp} from 'vue'
import '@fontsource-variable/inter/wght.css'
import '@fontsource-variable/inter/wght-italic.css'
import './assets/fonts/frutiger/frutiger.css'
import './style.css'
import DataObjectHTML from './components/DataObjectHTML.vue';
import DataObjectImage from './components/DataObjectImage.vue';
import DataObjectMovie from './components/DataObjectMovie.vue';
import DataObjectPlaylist from './components/DataObjectPlaylist.vue';
import DataObjectSound from "./components/DataObjectSound.vue";
import DataObjectDigitalClock from './components/DataObjectDigitalClock.vue';
import DataObjectContainer from './components/DataObjectContainer.vue';
import App from './App.vue';
import {createPinia} from "pinia";
import router from './router';
import DataObjectLinkPage from "./components/DataObjectLinkPage.vue";
import DataObjectAlternating from "./components/DataObjectAlternating.vue";
import ComponentWrapper from "./components/ComponentWrapper.vue";
import DataObjectText from "./components/DataObjectText.vue";
import ElementRenderer from "./components/ElementRenderer.vue";
import DataObjectDatasheet from "./components/DataObjectDatasheet.vue";
import DataObjectDatasheetRow from "./components/DataObjectDatasheetRow.vue";
import DataObjectButton from "./components/DataObjectButton.vue";
import DataObjectTicker from "./components/DataObjectTicker.vue";
import DataObjectProgressBar from "./components/DataObjectProgressBar.vue";
import DataObjectCountdown from "./components/DataObjectCountdown.vue";
import DataObjectAnalogClock from "./components/DataObjectAnalogClock.vue";
import DataObjectTimer from "./components/DataObjectTimer.vue";
import {FeatureFlags} from "./utils/FeatureFlags";
import {killStructuredCache} from "./cache/CacheControl";
import {registerSW} from "virtual:pwa-register";
import {isTauri} from "@tauri-apps/api/core";
import DataObjectInputText from "./components/DataObjectInputText.vue";
import {integrationManager} from "./integrations/IntegrationManager";
import AuthenticationPage from "./components/AuthenticationPage.vue";
import useStore from "./store.ts";
import {socketManager} from "./services/managers";
import * as Sentry from "@sentry/vue";
import DataObjectEditor from "./components/DataObjectEditor.vue";
import DataObjectSubmitButton from "./components/DataObjectSubmitButton.vue";


const pinia = createPinia()
const app = createApp(App);

void integrationManager.init();

app.component('ComponentWrapper', ComponentWrapper);
app.component('DataObjectDigitalClock', DataObjectDigitalClock);
app.component('DataObjectHtml', DataObjectHTML)
app.component('DataObjectImage', DataObjectImage)
app.component('DataObjectMovie', DataObjectMovie)
app.component('DataObjectContainer', DataObjectContainer)
app.component('DataObjectPlaylist', DataObjectPlaylist)
app.component('DataObjectSound', DataObjectSound)
app.component('DataObjectText', DataObjectText)
app.component('ElementRenderer', ElementRenderer)
app.component('DataObjectLinkPage', DataObjectLinkPage)
app.component('DataObjectDatasheet', DataObjectDatasheet)
app.component('DataObjectEditor', DataObjectEditor)
app.component('DataObjectDatasheetRow', DataObjectDatasheetRow)
app.component('DataObjectAlternating', DataObjectAlternating)
app.component('DataObjectButton', DataObjectButton)
app.component('DataObjectSubmitButton', DataObjectSubmitButton)
app.component('DataObjectTicker', DataObjectTicker)
app.component('DataObjectProgressBar', DataObjectProgressBar)
app.component('DataObjectCountdown', DataObjectCountdown)
app.component('DataObjectAnalogClock', DataObjectAnalogClock)
app.component('DataObjectTimer', DataObjectTimer)
app.component('DataObjectInputText', DataObjectInputText)
app.component('AuthenticationPage', AuthenticationPage)

if (typeof window !== "undefined") {
    // Expose a kill-switch for manual validation
    (window as any).KILL_CACHE = killStructuredCache;
}

if (typeof window !== "undefined") {
    // Browsers only: the widget loads from file:// and the Tauri shell has no service worker on any OS.
    const servedOverHttp = window.location.protocol === "http:" || window.location.protocol === "https:";
    const shouldRegisterServiceWorker = FeatureFlags.mediaServiceWorker && servedOverHttp && !isTauri();

    if (shouldRegisterServiceWorker) {
        // Ensure tree-shaking keeps registration reference
        (window as any).__lumidsSW = registerSW({
            immediate: true,
            onRegistered() {
                console.info("[ServiceWorker] CacheWorker registered");
            },
            onRegisterError(err) {
                console.warn("[ServiceWorker] registration failed", err);
            },
            onNeedRefresh() {
                console.info("[ServiceWorker] new version available");
            },
            onOfflineReady() {
                console.info("[ServiceWorker] offline ready");
            },
        });
    } else if (FeatureFlags.mediaServiceWorker) {
        console.info("[ServiceWorker] skipped (not a browser origin)", window.location.protocol);
    }
}

if (typeof window !== "undefined") {
    let forceReloadIssued = false;

    window.addEventListener("app:force-reload", () => {
        if (forceReloadIssued) return;
        forceReloadIssued = true;
        (window as any).__lumidsForceReloadHandled = true;
        window.location.reload();
    });

    window.addEventListener("auth:required", () => {
        if (router.currentRoute.value.path !== "/auth") {
            void router.push("/auth");
        }
    });

    window.addEventListener("online", () => {
        const store = useStore();
        console.log("[App] Internet Connection has recovered.");
        store.socketManager.kick("online");
    });
}

Sentry.init({
    app,
    dsn: "https://f86eafa7819a6cc4aad23748c086e2bd@o4511381648572416.ingest.de.sentry.io/4511381651325008",
    sendDefaultPii: false,
    integrations: [
        Sentry.browserTracingIntegration({ router }),
    ],
    tracesSampleRate: 0.05,
    tracePropagationTargets: ["localhost", socketManager.getURL()],
    enableLogs: false,
});


app.use(pinia)
app.use(router)

const mountApp = async () => {
    if (typeof document === "undefined") {
        return;
    }

    const mountTarget = document.getElementById("app");
    if (!mountTarget) {
        throw new Error("Missing #app mount element");
    }

    await router.isReady();
    app.mount(mountTarget);
};

void mountApp();